import { ComponentProps } from 'react'

import { useFragment } from '@apollo/client'
import { DeepPartial } from '@apollo/client/utilities'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { FinanceContactFragment, FinanceContactFragmentDoc } from 'generated/resources/graphql'

import { countryName } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

interface ContactInfoProps {
  id: number
}

const formatAddress = ({
  line1,
  line2,
  postalCode,
  city,
  state,
}: DeepPartial<FinanceContactFragment['address']>) => {
  const attrs = line2 ? [line1, line2, postalCode, city, state] : [line1, postalCode, city, state]
  return attrs.map((atr) => atr || '-').join(', ')
}

const formatIban = (iban: string | null | undefined) => {
  if (!iban) return '-'

  const parts = iban.match(/.{1,4}/g)
  return parts ? parts.join(' ') : ''
}

const ContactInfo = ({ id }: ContactInfoProps) => {
  const { data } = useFragment({
    fragment: FinanceContactFragmentDoc,
    fragmentName: 'FinanceContact',
    from: {
      __typename: 'FinanceContact',
      id,
    },
    optimistic: false,
  })

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    data?.legalName && {
      title: i18n.t('procurement.purchase_orders.detail.vendor.fiscal_name'),
      content: { text: data.legalName, type: 'item' as const },
    },
    data?.taxId && {
      title: i18n.t('procurement.purchase_orders.detail.vendor.tin'),
      content: { text: data.taxId, type: 'item' as const },
    },
    data?.address && {
      title: i18n.t('procurement.purchase_orders.detail.vendor.address'),
      content: { text: formatAddress(data.address), type: 'item' as const },
    },
    data?.address?.countryCode && {
      title: i18n.t('procurement.purchase_orders.detail.vendor.country'),
      content: { text: countryName(data.address.countryCode), type: 'item' as const },
    },
    ...(data.preferredPaymentMethod === 'banktransfer'
      ? [
          {
            title: i18n.t('procurement.purchase_orders.detail.vendor.iban'),
            content: { text: formatIban(data.iban), type: 'item' as const },
          },
          {
            title: i18n.t('procurement.purchase_orders.detail.vendor.bank_code'),
            content: { text: data.bankCode || '-', type: 'item' as const },
          },
        ]
      : []),
  ].filter(Boolean)

  return (
    <Box gap="s16">
      <Text.Header3>{i18n.t('finance.vendors.vendor_detail.title')}</Text.Header3>
      <DetailsItemsList details={details} tableView />
    </Box>
  )
}

export default ContactInfo
